/**
 * Хранилище UI-состояния (Zustand).
 * Управляет темой оформления, состоянием боковой панели, локалью
 * и пользовательскими настройками отображения данных.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'dark' | 'light' | 'system';
type Locale = 'ru' | 'en';
type Density = 'compact' | 'default' | 'comfortable';
type AutoRefresh = 'off' | '30s' | '1m' | '5m';
type DefaultDateRange = '7d' | '30d' | '90d' | '1y' | 'all';

interface UIState {
  /** Текущая тема */
  theme: Theme;
  /** Боковая панель свёрнута */
  sidebarCollapsed: boolean;
  /** Боковая панель свёрнута по умолчанию */
  sidebarCollapsedDefault: boolean;
  /** Текущая локаль */
  locale: Locale;
  /** Плотность данных в таблицах */
  density: Density;
  /** Количество строк на странице по умолчанию */
  defaultPerPage: number;
  /** Диапазон дат по умолчанию для отчётов */
  defaultDateRange: DefaultDateRange;
  /** Интервал автообновления данных */
  autoRefresh: AutoRefresh;
  /** Email-уведомления */
  emailNotifications: boolean;
  /** Звук при новом уведомлении */
  soundNotifications: boolean;
}

interface UIActions {
  /** Установить тему */
  setTheme: (theme: Theme) => void;
  /** Переключить тему (для обратной совместимости) */
  toggleTheme: () => void;
  /** Переключить боковую панель */
  toggleSidebar: () => void;
  /** Установить локаль */
  setLocale: (locale: Locale) => void;
  /** Установить плотность данных */
  setDensity: (density: Density) => void;
  /** Установить свёрнутость сайдбара по умолчанию */
  setSidebarCollapsedDefault: (collapsed: boolean) => void;
  /** Установить количество строк на странице */
  setDefaultPerPage: (perPage: number) => void;
  /** Установить диапазон дат по умолчанию */
  setDefaultDateRange: (range: DefaultDateRange) => void;
  /** Установить интервал автообновления */
  setAutoRefresh: (interval: AutoRefresh) => void;
  /** Установить email-уведомления */
  setEmailNotifications: (enabled: boolean) => void;
  /** Установить звук уведомлений */
  setSoundNotifications: (enabled: boolean) => void;
}

type UIStore = UIState & UIActions;

/** Вычисляет реальную тему на основе настройки */
function resolveTheme(theme: Theme): 'dark' | 'light' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

/**
 * Хранилище UI.
 * Настройки сохраняются в localStorage.
 */
export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      theme: 'dark',
      sidebarCollapsed: false,
      sidebarCollapsedDefault: false,
      locale: 'ru',
      density: 'default',
      defaultPerPage: 25,
      defaultDateRange: '30d',
      autoRefresh: 'off',
      emailNotifications: true,
      soundNotifications: false,

      setTheme: (theme) =>
        set(() => {
          const resolved = resolveTheme(theme);
          document.documentElement.setAttribute('data-theme', resolved);
          return { theme };
        }),

      toggleTheme: () =>
        set((state) => {
          const currentResolved = resolveTheme(state.theme);
          const newTheme = currentResolved === 'dark' ? 'light' : 'dark';
          document.documentElement.setAttribute('data-theme', newTheme);
          return { theme: newTheme };
        }),

      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setLocale: (locale) => set({ locale }),

      setDensity: (density) => set({ density }),

      setSidebarCollapsedDefault: (collapsed) =>
        set({ sidebarCollapsedDefault: collapsed, sidebarCollapsed: collapsed }),

      setDefaultPerPage: (perPage) => set({ defaultPerPage: perPage }),

      setDefaultDateRange: (range) => set({ defaultDateRange: range }),

      setAutoRefresh: (interval) => set({ autoRefresh: interval }),

      setEmailNotifications: (enabled) => set({ emailNotifications: enabled }),

      setSoundNotifications: (enabled) => set({ soundNotifications: enabled }),
    }),
    {
      name: 'asubp-ui', // ключ в localStorage
      onRehydrateStorage: () => {
        return (state) => {
          // При восстановлении состояния устанавливаем data-theme
          if (state) {
            const resolved = resolveTheme(state.theme);
            document.documentElement.setAttribute('data-theme', resolved);
          }
        };
      },
    },
  ),
);
